import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { IAppState } from 'core/store/store';
import styles from './PackageFeatures.module.scss';
import { Accordion, Button, ButtonType, ModalTypes, Spinner, StandardModal, Tooltip } from '@digicert/dcone-common-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/pro-solid-svg-icons';
import { faExternalLinkSquare } from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import { IAccessScope } from 'reducers/Reducers.interfaces';
import { renderManagerIcon, translate, translateWithParams } from 'shared/helpers/utils';
import { tlmPackages, stmPackages, packageTypes } from './PackageFeatures.constants';
import { IPackageDetailsProps, IPackageType, IActivePackage, ICategory, IFeature } from './PackageFeatures.types';
import { CONTACT_SALES_URL } from 'core/utils/constants';
import { fetchPackageDetails, addPackage } from '../../Tenants.actions';
import { ApplicationKeyMapping, Applications } from '../Tenant.types';
import { ApplicationPermissionMap } from 'core/selectors/Profile.selectors';

export const PackageFeatures = ({ profile, match, applications, fetchPackageDetails, addPackage }: IPackageDetailsProps) => {
  const [packageTypesState, setPackageTypesState] = useState<IPackageType[]>(packageTypes);
  const [activePackages, setActivePackages] = useState<IActivePackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<IPackageType | null>(null);
  const { id: accountId, applicationId } = match.params;
  const appCode = applications.find((application) => application.id === applicationId)?.app_code || '';
  const columnCount = (packageTypesState?.length || 0) + 1;
  const columnStyle = { gridTemplateColumns: `repeat(${columnCount}, 1fr)` };
  const [updateType, setUpdateType] = useState('');
  const packageList = appCode === Applications.SSM ? stmPackages : tlmPackages
  const packageFeatureType = appCode === Applications.SSM ? 'stmPackageFeatures' : 'tlmPackageFeatures';
  const packageCategoryType = appCode === Applications.SSM ? 'stmCategories' : 'tlmCategories';
  const isAdvancedSTMDisabled = (appCode: string, packageName: string) =>
    appCode === Applications.SSM && packageName === 'advanced';

  useEffect(() => {
    fetchPackageDetails(accountId).then((response) => {
      setActivePackages(response?.packages);
    })
  }, []);

  useEffect(() => {
    const updatedPackageTypes = packageTypes
      .map((pkg: IPackageType) => ({
        ...pkg,
        active: activePackages?.some(
          (activePkg: IActivePackage) =>
            activePkg.application_code === appCode &&
            activePkg.package_code.includes(pkg.package_name)
        ),
      }))
      .filter((pkg: IPackageType, _, arr) => {
        const activePackageType = arr.find((p) => p.active);
        return activePackageType
          ? activePackageType.canUpgradeTo.includes(pkg.package_id) || pkg.active
          : true
      })
    setPackageTypesState(updatedPackageTypes)
  }, [profile, appCode, activePackages])

  const add = (): void => {
    setLoading(true);
    addPackage(
      accountId,
      applicationId,
      `${ApplicationKeyMapping[appCode]}_${selectedPackage?.package_name}`
    ).then(() => {
      setSelectedPackage(null);
      fetchPackageDetails(accountId).then((updatedResponse) => {
        setActivePackages(updatedResponse.packages);
        setLoading(false);
      })
    })
  };

  const getAppIcon = (): string => {
    const app = applications.find((application) => application.app_code === appCode);
    return app ? app.icon : '';
  };

  const updatePkg = (pkg: IPackageType, updateType: string) => {
    setUpdateType(updateType);
    setSelectedPackage(pkg);
  }

  const getButton = (pkg: IPackageType) => {
    if (profile.access_scope === IAccessScope.account) {
      if (!pkg.active) {
        return (
          <a href={CONTACT_SALES_URL} target="_blank" rel="noopener noreferrer" className={styles.link}>
            <Button buttonType={ButtonType.SECONDARY}>
              {translate('dashboardLicense.packageFeatures.buttons.contactSales')}{' '}
              <FontAwesomeIcon className={styles.linkIcon} icon={faExternalLinkSquare} />
            </Button>
          </a>
        );
      } else {
        return (
          <Button buttonType={ButtonType.SECONDARY} disabled>
            {translate('dashboardLicense.packageFeatures.buttons.currentPlan')}
          </Button>
        );
      }
    } else if ((profile.access_scope === IAccessScope.system || profile.access_scope === IAccessScope.partner) && profile?.permissions?.find(perm => perm.name === ApplicationPermissionMap.canManageAccount)) {
      if (!activePackages?.some((activePkg: IActivePackage) => activePkg.application_code === appCode)) {
        return (
          <Button buttonType={ButtonType.SECONDARY} onClick={() => { updatePkg(pkg, 'addPackage') }} disabled={isAdvancedSTMDisabled(appCode, pkg.package_name)}>
            {translate('dashboardLicense.packageFeatures.buttons.enable')}
          </Button>
        );
      } else if (pkg.active) {
        return (
          <Button buttonType={ButtonType.SECONDARY} disabled>
            {translate('dashboardLicense.packageFeatures.buttons.currentPlan')}
          </Button>
        );
      } else {
        return (
          <Button buttonType={ButtonType.SECONDARY} onClick={() => { updatePkg(pkg, 'upgradePackage') }} disabled={isAdvancedSTMDisabled(appCode, pkg.package_name)}>
            {translate('dashboardLicense.packageFeatures.buttons.upgradePackage')}
          </Button>
        );
      }
    }
    return null;
  };

  return (
    <section className={styles.packageTable}>
      <h3 className={styles.packageHeading}>
        {translate(`dashboardLicense.packageFeatures.applications.${appCode}`)}&nbsp;<span>{translate('dashboardLicense.packageFeatures.packages')}</span>
      </h3>
      {/* === Top Row for 'Current Package' Labels === */}
      <div className={styles.currentPackageRow} style={columnStyle}>
        <div /> {/* empty top-left cell */}
        {packageTypesState?.map((pkg, idx) => (
          <div key={idx} className={pkg.active ? styles.currentPackage : ''}>
            {pkg.active && translate('dashboardLicense.packageFeatures.currentPackage')}
          </div>
        ))}
      </div>
      {/* Header Row */}
      <div className={styles.header} style={columnStyle}>
        <div className={styles.title}>
          <div className={styles.appDetails}>
            <i className={styles.appIcon}>
              {renderManagerIcon(
                getAppIcon(),
                translate(`dashboardLicense.packageFeatures.applications.${appCode}`)
              )}
            </i>
            <p className={styles.app}>
              {translate(`dashboardLicense.packageFeatures.applications.${appCode}`)}
            </p>
          </div>
        </div>
        {packageTypesState?.map((pkg: IPackageType) => (
          <div key={pkg.package_id} className={styles.packageCard}>
            <div className={styles.packageName}>
              <div>{translate(`dashboardLicense.packageFeatures.${pkg.package_name}`)}</div>
              {pkg.package_name === 'premium' && (
                <FontAwesomeIcon icon={faCrown} className={styles.crownIcon} />
              )}
            </div>
            <div className={styles.packageDesc}>
              {translate(`dashboardLicense.packageFeatures.description1.${pkg.package_name}`)}
            </div>
            <div className={styles.packageDesc}>
              {translate(`dashboardLicense.packageFeatures.description2.${pkg.package_name}`)}
            </div>
            <div className={styles.actionContainer}>{getButton(pkg)}</div>
          </div>
        ))}
      </div>
      {packageList.map((cat: ICategory, index: number) => (
        <Accordion
          key={cat.categoryId}
          oneByOne={false}
          defaultActiveKey={index === 0 ? cat.categoryId : ''}
          accordionData={[
            {
              headline: translate(`dashboardLicense.packageFeatures.${packageCategoryType}.${cat.categoryId}`) as string,
              key: cat.categoryId,
              view: (
                <div>
                  {cat.features.map((feature: IFeature) => (
                    <div
                      key={feature.featureId}
                      className={styles.featureRow}
                      style={columnStyle}
                    >
                      <Tooltip title={translate(`dashboardLicense.packageFeatures.${packageFeatureType}.description.${feature.featureId}`)} placement='top'>
                        <div className={styles.featureName}>{translate(`dashboardLicense.packageFeatures.${packageFeatureType}.${feature.featureId}`)}</div>
                      </Tooltip>
                      {packageTypesState.map((level: IPackageType) => (
                        <div key={level.package_id}>
                          {typeof feature[level.package_name] === 'boolean' && feature[level.package_name] === true ?
                            <FontAwesomeIcon icon={faCheckCircle} className={styles.checkIcon} /> :
                            typeof feature[level.package_name] !== 'boolean' ? feature[level.package_name] : ''}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      ))}
      {loading && <Spinner />}
      <StandardModal
        modalType={ModalTypes.STANDARD}
        headline={translateWithParams(`dashboardLicense.packageFeatures.${updateType}`, { package: translate(`dashboardLicense.packageFeatures.${selectedPackage?.package_name}`) }) as string}
        display={selectedPackage ? true : false}
        buttonLabel={translateWithParams(`dashboardLicense.packageFeatures.buttons.${updateType}`, { package: translate(`dashboardLicense.packageFeatures.${selectedPackage?.package_name}`) }) as string}
        buttonCancelLabel={translate('common.form.buttons.cancelBtn') as string}
        onOkCallBack={add}
        onCancelCallBack={() => setSelectedPackage(null)}
      >
        <p>{translateWithParams('dashboardLicense.packageFeatures.addPackageDescription', { package: translate(`dashboardLicense.packageFeatures.${selectedPackage?.package_name}`), application: translate(`dashboardLicense.packageFeatures.applications.${appCode}`) })}</p>
      </StandardModal>
    </section>
  );
};

const mapStateToProps = (state: IAppState) => ({
  profile: state.profile.data,
  applications: state.userApplications.data
});

const mapDispatchToProps = {
  fetchPackageDetails,
  addPackage
};

export default connect(mapStateToProps, mapDispatchToProps)(PackageFeatures);